'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { changeInsectCountThreshold, fetchUserInfo } from '../redux/slices/dataSlice';
import insect_green from '../assets/images/insect_green.png';
import insect_yellow from '../assets/images/insect_yellow.png';
import insect_red from '../assets/images/insect_red.png';
import insect_dark_red from '../assets/images/insect_dark_red.png';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

export default function AccountThreshold() {
  const { t } = useTranslation(['account', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.data.userInfo);
  
  // State for threshold ranges
  const [greenHigh, setGreenHigh] = useState(1);
  const [yellowHigh, setYellowHigh] = useState(5);
  const [lightRedHigh, setLightRedHigh] = useState(7);
  
  // Disable states for buttons
  const [decDisableGreen, setDecDisableGreen] = useState(false);
  const [incDisableGreen, setIncDisableGreen] = useState(false);
  const [decDisableYellow, setDecDisableYellow] = useState(false);
  const [incDisableYellow, setIncDisableYellow] = useState(false);
  const [decDisableLightRed, setDecDisableLightRed] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);

  // Load user thresholds on component mount
  useEffect(() => {
    dispatch(fetchUserInfo());
  }, [dispatch]);

  // Update local state when user info changes
  useEffect(() => {
    if (userInfo) {
      setGreenHigh(userInfo.yellow || 1);
      setYellowHigh(userInfo.lightRed || 5);
      setLightRedHigh(userInfo.darkRed || 7);
    }
  }, [userInfo]);

  // Set default values
  const setDefaultValues = () => {
    setGreenHigh(1);
    setYellowHigh(5);
    setLightRedHigh(7);
    setButtonDisabled(false);
  };

  // Save thresholds
  const changeThresholds = async () => {
    setLoading(true);

    const payload = {
      yellow: greenHigh,
      lightRed: yellowHigh,
      darkRed: lightRedHigh,
    };

    try {
      await dispatch(changeInsectCountThreshold(payload)).unwrap();
      await dispatch(fetchUserInfo()).unwrap();
      setButtonDisabled(true);
    } catch (error) {
      logger.error('Failed to update thresholds:', error);
    } finally {
      setLoading(false);
    }
  };

  // Button disable logic for green
  useEffect(() => {
    setDecDisableGreen(greenHigh === 0);
    setIncDisableGreen(greenHigh + 1 === yellowHigh);
  }, [greenHigh, yellowHigh]);

  // Button disable logic for yellow
  useEffect(() => {
    setDecDisableYellow(greenHigh + 1 === yellowHigh);
    setIncDisableYellow(yellowHigh + 1 === lightRedHigh);
  }, [greenHigh, yellowHigh, lightRedHigh]);

  // Button disable logic for light red
  useEffect(() => {
    setDecDisableLightRed(yellowHigh + 1 === lightRedHigh);
  }, [yellowHigh, lightRedHigh]);

  // Check if values have changed from server values
  useEffect(() => {
    if (userInfo) {
      const hasChanged = 
        greenHigh !== (userInfo.yellow || 1) ||
        yellowHigh !== (userInfo.lightRed || 5) ||
        lightRedHigh !== (userInfo.darkRed || 7);
      setButtonDisabled(!hasChanged);
    }
  }, [greenHigh, yellowHigh, lightRedHigh, userInfo]);

  return (
    <div className="space-y-6">
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('account:insectCountThreshold')}</h3>
        
        <p className="text-sm text-gray-900 mb-6">
          {t('account:configureAlertLevels')}
        </p>

        <div className="space-y-4">
          {/* Green Range */}
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <img 
                src={insect_green} 
                alt="Green Insect" 
                width={32}
                height={32}
                className="w-8 h-8"
              />
              <div>
                <span className="text-sm font-medium text-green-800">
                  {t('account:range')}: 0-{greenHigh}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (greenHigh > 0) {
                    setGreenHigh(greenHigh - 1);
                  }
                }}
                disabled={decDisableGreen}
                className={`w-8 h-8 rounded-md font-medium ${
                  decDisableGreen
                    ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                }`}
              >
                -
              </button>
              <button
                onClick={() => {
                  setGreenHigh(greenHigh + 1);
                }}
                disabled={incDisableGreen}
                className={`w-8 h-8 rounded-md font-medium ${
                  incDisableGreen
                    ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                }`}
              >
                +
              </button>
            </div>
          </div>

          {/* Yellow Range */}
          <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <img 
                src={insect_yellow} 
                alt="Yellow Insect" 
                width={32}
                height={32}
                className="w-8 h-8"
              />
              <div>
                <span className="text-sm font-medium text-yellow-800">
                  {t('account:range')}: {greenHigh + 1}-{yellowHigh}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setYellowHigh(yellowHigh - 1);
                }}
                disabled={decDisableYellow}
                className={`w-8 h-8 rounded-md font-medium ${
                  decDisableYellow
                    ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                }`}
              >
                -
              </button>
              <button
                onClick={() => {
                  setYellowHigh(yellowHigh + 1);
                }}
                disabled={incDisableYellow}
                className={`w-8 h-8 rounded-md font-medium ${
                  incDisableYellow
                    ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                }`}
              >
                +
              </button>
            </div>
          </div>

          {/* Light Red Range */}
          <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <img 
                src={insect_red} 
                alt="Red Insect" 
                width={32}
                height={32}
                className="w-8 h-8"
              />
              <div>
                <span className="text-sm font-medium text-orange-800">
                  {t('account:range')}: {yellowHigh + 1}-{lightRedHigh}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setLightRedHigh(lightRedHigh - 1);
                }}
                disabled={decDisableLightRed}
                className={`w-8 h-8 rounded-md font-medium ${
                  decDisableLightRed
                    ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
                }`}
              >
                -
              </button>
              <button
                onClick={() => {
                  setLightRedHigh(lightRedHigh + 1);
                }}
                className="w-8 h-8 rounded-md font-medium bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              >
                +
              </button>
            </div>
          </div>

          {/* Dark Red Range */}
          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <img 
                src={insect_dark_red} 
                alt="Dark Red Insect" 
                width={32}
                height={32}
                className="w-8 h-8"
              />
              <div>
                <span className="text-sm font-medium text-red-800">
                  {t('account:range')}: {lightRedHigh + 1}+
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={setDefaultValues}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors cursor-pointer"
          >
            {t('account:default')}
          </button>
          <button
            onClick={changeThresholds}
            disabled={buttonDisabled || loading}
            className={`px-4 py-2 rounded-md transition-colors ${
              buttonDisabled || loading
                ? 'bg-gray-300 text-gray-800 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600 cursor-pointer'
            }`}
          >
            {loading ? t('account:saving') : t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
}